import { DealValue, DealValueAreEqual, DealValueComparisonKey } from "src/app/features/prob/models/deal-value";
import { RateItem } from "src/app/features/prob/models/rate-item";
import { GridStateConfig } from "@mysrc/app/features/prob/models/grid-state-config";
import { FormMetaData } from "src/app/features/prob/models/form-meta-data";
import { DealItemDataChanges } from "./deal-item-data-changes";
import { DealValueGridData, EnumControlTypes, EnumDataTypes, EnumModelCodes } from ".";
import { hasChange } from "@progress/kendo-angular-dateinputs/util";
import { CascadeModelItem } from "./cascade-model-Item";
import { ChangeDetectorRef } from "@angular/core";

/**
 * Manages the state data for a deal item, including both value and data to rebuild the form.
 * Incorporates the deal item identifier, deal values, rate items, form meta data, and grid table meta state data.
 */
export class DealItemState {

  // ----------------------- Constants ----------------------------------------

  /** Initial seed value for client-generated composite keys */
  protected readonly ctInitialClientId = 1;

  /** Empty string guard value for varName checks */
  protected readonly ctEmptyString = '';

  // -------------------- Component State -------------------------------------

  /** Deal item display name */
  public name: string | null = null;

  /** Unique deal item identifier */
  public dealItemId: string | null = null;

  /** Indicates whether any grid contains invalid data */
  public isInvalidGridExists: boolean = false;

  /**
   * Client-side ID seed for composite key generation.
   * Used to track data sent to the server and as a lookup when the server-assigned ID is unavailable.
   */
  public clientId: number = 1;

  // ------------------- Deal Value Collections -------------------------------

  /** Active deal values indexed by composite comparison key */
  public dealValues = new Map<string, DealValue>();

  /** Pending deal value changes indexed by composite comparison key */
  public dealValueChanges = new Map<string, DealValue>();

  /**
   * First-seen dtVal for Text-controlCode DateTime fields (e.g. "datecreated"),
   * indexed by modelValueId. These fields are set once at creation and must
   * never change afterward - pinning the value here (rather than relying on
   * DealValueComparisonKey matching, which varies with createdFor/live-draft
   * state) guarantees it survives any later sync, regardless of why a later
   * sync might otherwise bring in a different value.
   */
  protected pinnedCreatedDates = new Map<string, Date>();

  /** Externally injected deal values preserved across sync cycles */
  protected storedInjectedValues: DealValue[] = [];

  // -------------------- Rate Item Collections -------------------------------

  /** Current rate item entries for this deal item */
  public rateItems: RateItem[] = [];

  /** Rate item identifiers that have been modified */
  public idsChanged = new Set<string>();

  /** Rate item identifiers that have been removed */
  public idsRemoved = new Set<string>();

  // ------------------- Form & Grid Meta Data --------------------------------

  /** Form meta data and data lookup optimizations */
  public formMetaData?: FormMetaData | null;

  /** Grid table states, validations, row defaults and data lookup optimizations indexed by model value grid table ID */
  public gridStatesMap = new Map<string, GridStateConfig>();

  /** Default time segment value when not specified by records */
  public timeSegmentDefault: number = 1;

  // ------------------- Cascade Collections ----------------------------------

  /**
   * Map of rate IDs to their cascading validation items for cascading dropdowns.
   * Outer key: varName (grid configuration variable name).
   * Inner key: 'val' property of root-level cascade items.
   * Inner value: a CascadeModelItem containing children as Map<string, CascadeModelItem[]>.
   */
  public cascadeJsonItem: Map<string, Map<string, CascadeModelItem>> = new Map();

  /**
   * Sorted column indexes for cascading dropdowns per tower.
   * Uses colOrder to determine left-to-right cascade order.
   */
  public colsCascade: Map<string, Map<number, string>> = new Map();

  // -------------------- State Management ------------------------------------

  /**
   * Resets change-tracking collections while preserving existing data.
   */
  public reset(): void {

    this.clientId = this.ctInitialClientId;
    this.idsChanged.clear();
    this.idsRemoved.clear();

    this.dealValueChanges.clear();
  }

  /**
   * Returns true if there are unsaved changes in the state.
   */
  public hasChanges(): boolean {

    if (this.dealValueChanges.size > 0)
      return true;

    const hasChanges = this.rateItems.some(x =>
      x.isEditItem === true
      || (
        x.idxVal1
        && (x.id === null
          || x.id === undefined
          || x.id.length < 1)
      )
      || (x.idxVal1 && !x.customerPrice)
    );

    if (hasChanges === true)
      return true;

    return this.idsChanged.size > 0 || this.idsRemoved.size > 0;
  }

  /**
   * Builds a change payload for saving the deal item.
   */
  public getDataChanges(): DealItemDataChanges | null {

    if (this.dealValueChanges.size < 1)
      return null;

    let changes: DealItemDataChanges = new DealItemDataChanges();

    changes.dealItemId = this.dealItemId;
    changes.dealValues = Array.from(this.dealValueChanges.values());
    changes.rateItems = this.rateItems.filter(x => x.isEditItem === true);

    return changes;
  }

  /**
   * Restores deal values from server data, clearing all local state.
   */
  public restoreFromServer(values: DealValue[] | null | undefined): void {

    this.dealValues.clear();
    this.dealValueChanges.clear();

    if (!values
      || values.length === 0)
      return;

    for (const value of values) {
      this.dealValues.set(DealValueComparisonKey(value), value);
    }
  }

  // --------------- Injected Value Management --------------------------------

  /** Returns the currently stored injected values */
  public getInjectedValues(): DealValue[] {

    return this.storedInjectedValues;
  }

  /**
   * Merges external values into the deal item state, preserving previously stored entries.
   */
  public setInjectedValues(injectedValues: DealValue[] | null | undefined): void {

    if (!injectedValues
      || injectedValues.length < 1)
      return;

    for (const value of injectedValues) {

      const key = DealValueComparisonKey(value);
      this.dealValues.set(key, value);

      const existingIdx = this.storedInjectedValues.findIndex(v =>
        v.code === value.code
        && v.dealItemId === value.dealItemId
      );

      if (existingIdx >= 0) {
        this.storedInjectedValues[existingIdx] = value;
      } else {
        this.storedInjectedValues.push(value);
      }
    }
  }

  /**
   * Replaces only the currency-related injected values, preserving all others.
   * Called after save/calculate when currency may have changed.
   */
  public updateInjectedCurrencyValues(currencyValues: DealValue[] | null): void {

    if (!currencyValues
      || currencyValues.length < 1)
      return;

    this.storedInjectedValues = this.storedInjectedValues.filter(v =>
      v.code !== EnumModelCodes.CurrencySymbol
      && v.code !== EnumModelCodes.CurrencyCode
    );

    this.storedInjectedValues = [...this.storedInjectedValues, ...currencyValues];

    for (const value of currencyValues) {
      const key = DealValueComparisonKey(value);
      this.dealValues.set(key, value);
    }
  }

  // -------------------- Form Meta Data --------------------------------------

  /**
   * Initializes the form meta data for this deal item state.
   * Intended to be called after the deal form state has been loaded.
   */
  public initFormMeta(isLive: boolean, injectedValues: DealValue[] | null | undefined): FormMetaData {

    if (this.formMetaData == null)
      this.formMetaData = new FormMetaData();

    this.formMetaData.dataConfig.isLive = isLive;
    this.formMetaData.dataConfig.dealItemId = this.dealItemId;

    const dealValues = Array.from(this.dealValues.values());
    this.formMetaData.dealValueRefreshLookups(dealValues, injectedValues);

    return this.formMetaData;
  }

  // ------------------- Cascade Operations -----------------------------------

  /** Checks whether cascade items exist for the given variable name */
  public hasCascadeItemsJson(varName?: string | null): boolean {

    if (!varName
      || varName === this.ctEmptyString)
      return false;

    return this.cascadeJsonItem.has(varName);
  }

  /** Returns the cascade item map for the given variable name */
  public getCascadeItemsJson(varName?: string | null): Map<string, CascadeModelItem> | null | undefined {

    if (!varName
      || varName === this.ctEmptyString)
      return null;

    if (this.cascadeJsonItem.has(varName))
      return this.cascadeJsonItem.get(varName);
    else
      return null;
  }

  /** Parses JSON data into a cascade item map and stores it for the given variable name */
  public setCascadeItemsJson(varName?: string | null, jsonData?: string | null): void {

    if (!varName
      || varName === this.ctEmptyString
      || !jsonData
      || jsonData === this.ctEmptyString)
      return;

    const cascades: Map<string, CascadeModelItem> = CascadeModelItem.createMapFromJson(jsonData);

    if (this.cascadeJsonItem.has(varName) == false)
      this.cascadeJsonItem.set(varName, cascades);
  }

  // --------------- Cascade Column Operations --------------------------------

  /** Checks whether cascade column data exists for the given variable name */
  public hasColsCascade(varName?: string | null): boolean {

    if (!varName
      || varName === this.ctEmptyString)
      return false;

    return this.colsCascade.has(varName);
  }

  /** Returns the cascade column configuration for the given variable name */
  public getColsCascade(varName?: string | null): Map<number, string> | null {

    if (!varName
      || varName === this.ctEmptyString)
      return null;

    let colCascadeConfig: (Map<number, string> | undefined | null) = this.colsCascade.get(varName);
    if (colCascadeConfig)
      return colCascadeConfig;

    return null;
  }

  /** Stores cascade column configuration for the given variable name */
  public setColsCascade(varName?: string | null, gridCol?: Map<number, string> | null): void {

    if (!varName
      || varName === this.ctEmptyString
      || !gridCol
      || gridCol === null)
      return;

    if (this.colsCascade.has(varName) == false)
      this.colsCascade.set(varName, gridCol);
  }

  // ------------------- Data Synchronization ---------------------------------

  /**
   * Syncs incoming deal values against existing state, tracking changes for inputs.
   * Returns true if any input values changed.
   *
   * TODO: Does not detect grid row removals. Consider comparing row counts
   * before and after, and including the total row count in the save payload
   * so the server can prune excess rows.
   */
  /**
   * Syncs incoming deal values against existing state, tracking changes for inputs.
   * Returns true if any input values changed.
   *
   * TODO: Does not detect grid row removals. Consider comparing row counts
   * before and after, and including the total row count in the save payload
   * so the server can prune excess rows.
   */
  public syncValuesForChanges(isLoading: boolean, inputs?: DealValue[] | null): boolean {

    let hasChanges: boolean = false;

    if (!inputs
      || inputs.length < 1)
      return false;

    for (let i = 0; i < inputs.length; i++) {

      const inputValue = inputs[i];

      const keyInput = DealValueComparisonKey(inputValue);
      const modelConfig = this.formMetaData?.configMapByModelValueId.get(inputValue.modelValueId ?? this.ctEmptyString);
      if (!modelConfig) {
        // Injected values are ignored and should not be part of the changes
        continue;
      }

      // Text-controlCode Date fields (e.g. "datecreated") are set once at
      // creation and must never change afterward. DealValueComparisonKey
      // varies with createdFor/live-draft state, so matching on it isn't
      // reliable for pinning this value - pin by modelValueId instead,
      // independent of which key this particular sync resolves to.
      const isPinnedDateField = modelConfig.dataTypeCode === EnumDataTypes.DateTime
        && modelConfig.controlCode === EnumControlTypes.Text
        && modelConfig.code === EnumModelCodes.DateCreated;

      if (isPinnedDateField && inputValue.modelValueId) {

        const pinned = this.pinnedCreatedDates.get(inputValue.modelValueId);

        if (pinned != null) {
          inputValue.dtVal = pinned;
        } else if (inputValue.dtVal != null) {
          this.pinnedCreatedDates.set(inputValue.modelValueId, inputValue.dtVal);
        }
      }

      if (this.dealValues.has(keyInput)) {
        const existingValue = this.dealValues.get(keyInput);

        // Always replace with the fresh server payload on sync.
        inputValue.id = existingValue?.id;

        this.dealValues.set(keyInput, inputValue);

        // Only record as a change when this sync is NOT a load — during a load
        // we are populating the cache from authoritative server data, not
        // capturing user edits.
        if (isLoading == false
          && modelConfig?.isInput == true) {

          this.dealValueChanges.set(keyInput, inputValue);
          hasChanges = true;
        }
      }
      else {

        this.dealValues.set(keyInput, inputValue);

        if (isLoading == false
          && modelConfig?.isInput == true) {

          this.dealValueChanges.set(keyInput, inputValue);
          hasChanges = true;
        }
      }
    }

    return hasChanges;
  }

  /**
   * Syncs grid-specific deal and calc values, updating the invalid grid flag.
   * Returns true if any input values changed.
   */
  public syncGridValuesForChanges(isLoading: boolean, gridValues?: DealValueGridData[] | null): boolean {

    if (!gridValues
      || gridValues.length < 1)
      return false;

    let hasChanges: boolean = false;

    for (const gridValue of gridValues) {

      this.isInvalidGridExists = gridValue.isInvalidGridExist;

      hasChanges = this.syncValuesForChanges(isLoading, gridValue.dealValues);

      hasChanges = this.syncValuesForChanges(isLoading, gridValue.calcValues);
    }

    return hasChanges;
  }

  /**
   * Syncs grid calculation values against existing state.
   * Returns true if any input values changed.
   */
  /**
   * Syncs grid calculation values against existing state.
   * Returns true if any input values changed.
   */
  protected syncGrid_CalcValues(dealValues: DealValue[], hasChanges: boolean, isLoading: boolean): boolean {

    for (let i = 0; i < dealValues.length; i++) {

      const inputValue = dealValues[i];

      const keyInput = DealValueComparisonKey(inputValue);
      const modelConfig = this.formMetaData?.configMapByModelValueId.get(inputValue.modelValueId ?? this.ctEmptyString);

      if (!modelConfig) {
        // Injected values are ignored and should not be part of the changes
        continue;
      }

      if (this.dealValues.has(keyInput)) {

        // Only record as a change when this sync is NOT a load — during a load
        // we are populating the cache from authoritative server data, not
        // capturing user edits.
        if (isLoading == false
          && modelConfig?.isInput == true) {

          this.dealValueChanges.set(keyInput, inputValue);
          hasChanges = true;
        }

      } else {

        this.dealValues.set(keyInput, inputValue);

        if (isLoading == false
          && modelConfig?.isInput == true) {

          this.dealValueChanges.set(keyInput, inputValue);
          hasChanges = true;
        }
      }
    }

    return hasChanges;
  }

  // ------------------- Cascade Header Values ---------------------------------

  /**
   * Applies a cache-driven value update for a control whose code matches.
   * Used when a shared-cache change needs to land on a slide that has no
   * visible form control for the value (e.g. a child deal item where the
   * config's showToUser is false but a dependency is wired to the model code).
   *
   * Mutates the live DealValue in dataMapByVarNameCode, dealValues, and
   * also writes into dealValueChanges so the save payload picks it up —
   * mirroring how a user edit on a visible control is captured.
   *
   * Returns true when the underlying value actually changed.
   *
   * @param code   The model code to locate (e.g. EnumModelCodes.UseCustomAdjustment)
   * @param value  New value, coerced to the appropriate DealValue field by data type
   * @param dataTypeCode  The data type that determines which DealValue field to write
   */
  public applyCacheValueByCode(
    code: string,
    value: any,
    dataTypeCode: string | null | undefined
  ): boolean {

    if (!code
      || code === this.ctEmptyString) {

      return false;
    }

    const lookup = this.formMetaData?.dataConfig.dataMapByVarNameCode;
    const dealValues = lookup?.get(code);

    if (!dealValues
      || dealValues.length === 0) {

      // No existing DealValue for this code — nothing to mutate.
      // Future enhancement: synthesize a new DealValue from the model config.
      return false;
    }

    const dealValue = dealValues[0];
    const previousNVal = dealValue.nVal;
    const previousSVal = dealValue.sVal;

    // Write into the appropriate field. Mirrors the dataTypeCode switch
    // used by the form-component getFormData() path.
    switch (dataTypeCode) {

      case EnumDataTypes.Number: {

        const numeric = this.coerceToNumberOrNull(value);

        if (dealValue.nVal === numeric)
          return false;

        dealValue.nVal = numeric;
        break;
      }

      case EnumDataTypes.String: {

        const str = value == null ? null : String(value);

        if (dealValue.sVal === str)
          return false;

        dealValue.sVal = str;
        break;
      }

      default: {

        // For anything else, fall back to numeric since the existing
        // YesNo controls store as nVal. Extend as new types appear.
        const numeric = this.coerceToNumberOrNull(value);

        if (dealValue.nVal === numeric)
          return false;

        dealValue.nVal = numeric;
        break;
      }
    }

    // Push into the changes map so the save payload includes it.
    const key = DealValueComparisonKey(dealValue);
    this.dealValueChanges.set(key, dealValue);

    return true;
  }

  /** Coerces booleans / strings / numbers into a number or null for nVal */
  protected coerceToNumberOrNull(val: any): number | null {

    if (val == null
      || val === '')
      return null;

    if (typeof val === 'boolean')
      return val ? 1 : 0;

    if (typeof val === 'number')
      return isNaN(val) ? null : val;

    const num = Number(val);
    return isNaN(num) ? null : num;
  }

}
